import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useTweaks, DEFAULT_TWEAKS } from "@/context/TweaksContext";
import {
  Lock, LogOut, RotateCcw, ArrowLeft, Eye, EyeOff,
  Palette, Gauge, Image as ImageIcon, Zap, Sparkles, Check, Save,
  Database, Download, RefreshCw, FileJson, FileSpreadsheet,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------- Reusable UI primitives ---------- */
const Card = ({ children, title, icon: Icon, testId }) => (
  <section
    data-testid={testId}
    className="rounded-[20px] border border-white/10 bg-white/[0.03] p-6 backdrop-blur-2xl md:p-8"
  >
    <header className="mb-6 flex items-center gap-3 border-b border-white/5 pb-4">
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#fcd34d]/10 ring-1 ring-[#fcd34d]/30">
        {Icon && <Icon size={15} className="text-[#fcd34d]" />}
      </span>
      <h2 className="font-serif-display text-2xl font-light italic text-[#fef3c7]">{title}</h2>
    </header>
    <div className="space-y-5">{children}</div>
  </section>
);

const Row = ({ label, hint, children, testId }) => (
  <div data-testid={testId} className="flex items-start justify-between gap-6 border-b border-white/5 pb-5 last:border-none last:pb-0">
    <div className="min-w-0 flex-1">
      <div className="font-body text-sm font-medium text-[#fef3c7]">{label}</div>
      {hint && <div className="font-body mt-1 text-xs font-light leading-relaxed text-[#d4a373]/80">{hint}</div>}
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

const Toggle = ({ value, onChange, testId }) => (
  <button
    type="button"
    role="switch"
    aria-checked={value}
    data-testid={testId}
    onClick={() => onChange(!value)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${value ? "bg-[#c5a059]" : "bg-white/10"}`}
  >
    <span className={`inline-block h-5 w-5 transform rounded-full bg-[#fef3c7] shadow transition ${value ? "translate-x-5" : "translate-x-0.5"}`} />
  </button>
);

const Slider = ({ value, onChange, min = 0, max = 100, step = 1, testId }) => (
  <div className="flex items-center gap-3">
    <input
      data-testid={testId}
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="h-1 w-40 cursor-pointer appearance-none rounded-full bg-white/10 accent-[#fcd34d]"
    />
    <span className="font-body w-10 text-right text-xs text-[#fcd34d]">{value}</span>
  </div>
);

const Select = ({ value, onChange, options, testId }) => (
  <select
    data-testid={testId}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="rounded-md border border-white/10 bg-[#0a0805] px-3 py-1.5 font-body text-sm text-[#fef3c7] outline-none focus:border-[#fcd34d]"
  >
    {options.map((o) => (
      <option key={o.value} value={o.value} className="bg-[#0a0805]">{o.label}</option>
    ))}
  </select>
);

const TextInput = ({ value, onChange, testId, multiline }) => {
  const cls = "w-full rounded-md border border-white/10 bg-[#0a0805] px-3 py-2 font-body text-sm text-[#fef3c7] outline-none focus:border-[#fcd34d]";
  return multiline ? (
    <textarea data-testid={testId} rows={3} value={value} onChange={(e) => onChange(e.target.value)} className={`${cls} resize-y`} />
  ) : (
    <input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className={cls} />
  );
};

const ColorInput = ({ value, onChange, testId }) => (
  <div className="flex items-center gap-2">
    <input
      data-testid={testId}
      type="color"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-8 w-12 cursor-pointer rounded-md border border-white/10 bg-transparent"
    />
    <span className="font-body text-xs uppercase tracking-wider text-[#d4a373]">{value}</span>
  </div>
);

/* ---------- Database Export Card ---------- */
function ExportCard() {
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem("valholl.admin.apikey") || "");
  const [collections, setCollections] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const loadCollections = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await axios.get(`${API}/admin/collections`, {
        headers: { "X-Admin-Key": adminKey },
      });
      setCollections(res.data?.collections || []);
      sessionStorage.setItem("valholl.admin.apikey", adminKey);
    } catch (e) {
      setErr(e?.response?.status === 401 ? "API key incorrecta" : "No se pudo conectar con el backend");
      setCollections(null);
    } finally {
      setLoading(false);
    }
  };

  const download = async (path, filename) => {
    try {
      const res = await axios.get(`${API}${path}`, {
        headers: { "X-Admin-Key": adminKey },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e?.response?.status === 401 ? "API key incorrecta" : "Error en la descarga");
    }
  };

  return (
    <Card testId="card-export" title="Exportar Base de Datos" icon={Database}>
      <div className="space-y-2">
        <div className="font-body text-sm font-medium text-[#fef3c7]">API Key del backend (ADMIN_API_KEY)</div>
        <div className="flex items-stretch gap-2">
          <input
            data-testid="export-apikey"
            type="password"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            placeholder="X-Admin-Key del backend/.env"
            className="w-full rounded-md border border-white/10 bg-[#0a0805] px-3 py-2 font-body text-sm text-[#fef3c7] outline-none focus:border-[#fcd34d]"
          />
          <button
            data-testid="export-load"
            onClick={loadCollections}
            disabled={!adminKey || loading}
            className="inline-flex items-center gap-2 rounded-md bg-[#c5a059] px-4 py-2 font-body text-xs uppercase tracking-[0.3em] text-[#04070b] transition hover:bg-[#fcd34d] disabled:opacity-50"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
            Conectar
          </button>
        </div>
        {err && <p data-testid="export-error" className="font-body text-xs text-red-400">{err}</p>}
        <p className="font-body text-[10px] leading-relaxed text-[#d4a373]/70">
          Configura <code className="rounded bg-white/5 px-1.5 py-0.5 text-[#fcd34d]">ADMIN_API_KEY</code> en <code className="rounded bg-white/5 px-1.5 py-0.5 text-[#fcd34d]">backend/.env</code> y úsala aquí.
        </p>
      </div>

      {collections && (
        <div data-testid="export-collections" className="space-y-3 border-t border-white/5 pt-5">
          {collections.length === 0 && (
            <p className="font-body text-sm text-[#d4a373]/80">La base de datos está vacía.</p>
          )}
          {collections.map((c) => (
            <div key={c.name} className="flex items-center justify-between gap-3 rounded-lg border border-white/5 bg-white/[0.02] p-4">
              <div className="min-w-0">
                <div className="font-body text-sm font-medium text-[#fef3c7]">{c.name}</div>
                <div className="font-body text-[10px] uppercase tracking-[0.3em] text-[#d4a373]/70">
                  {c.count.toLocaleString("es-ES")} registros
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                <button
                  data-testid={`export-${c.name}-csv`}
                  onClick={() => download(`/admin/export/${c.name}.csv`, `${c.name}.csv`)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 font-body text-[10px] uppercase tracking-[0.3em] text-[#fcd34d] transition hover:border-[#fcd34d]/40 hover:bg-white/5"
                >
                  <FileSpreadsheet size={11} /> CSV
                </button>
                <button
                  data-testid={`export-${c.name}-json`}
                  onClick={() => download(`/admin/export/${c.name}.json`, `${c.name}.json`)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 font-body text-[10px] uppercase tracking-[0.3em] text-[#d4a373] transition hover:border-[#fcd34d]/40 hover:bg-white/5"
                >
                  <FileJson size={11} /> JSON
                </button>
              </div>
            </div>
          ))}

          {collections.length > 0 && (
            <button
              data-testid="export-all"
              onClick={() => download(`/admin/export/all.json`, `myvalhalla-full-dump.json`)}
              className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#fcd34d] px-4 py-3 font-body text-xs font-medium uppercase tracking-[0.3em] text-[#04070b] transition hover:bg-[#fef3c7]"
            >
              <Download size={13} /> Descargar volcado completo (JSON)
            </button>
          )}
        </div>
      )}
    </Card>
  );
}

const EDITABLE_IMAGES = [
  { id: "hero", label: "Imagen del Hero (Barco vikingo)", currentUrl: "/generated/hero_bg.jpg" },
  { id: "gates", label: "Puertas del Valhalla", currentUrl: "/generated/valhalla_gates_new.jpg" },
  { id: "paradise", label: "El Paraíso (Fondo interior)", currentUrl: "/generated/valhalla_paradise.jpg" },
  { id: "memory_0", label: "Álbum: Niños jugando", currentUrl: "/generated/memory_0.jpg" },
  { id: "memory_1", label: "Álbum: El primer nieto", currentUrl: "/generated/memory_1.jpg" },
  { id: "memory_2", label: "Álbum: Toda la familia", currentUrl: "/generated/memory_2.jpg" },
  { id: "memory_3", label: "Álbum: Verano en el mar", currentUrl: "/generated/memory_3.jpg" },
  { id: "memory_4", label: "Álbum: Nuestra boda", currentUrl: "/generated/memory_4.jpg" },
  { id: "memory_5", label: "Álbum: Padre e hijo", currentUrl: "/generated/memory_5.jpg" },
  { id: "story_0", label: "Cápsula: Para el día de tu boda", currentUrl: "/generated/story_0.jpg" },
  { id: "story_1", label: "Cápsula: Lo que aprendí de mi padre", currentUrl: "/generated/story_1.jpg" },
  { id: "story_2", label: "Cápsula: Para mis nietos", currentUrl: "/generated/story_2.jpg" },
  { id: "story_3", label: "Cápsula: Lo que la gratitud me enseñó", currentUrl: "/generated/story_3.jpg" },
];

function ImageManagementCard() {
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem("valholl.admin.apikey") || "");
  const [uploadingId, setUploadingId] = useState(null);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [refreshTimes, setRefreshTimes] = useState({});

  const handleUpload = async (id, file) => {
    if (!file) return;
    setUploadingId(id);
    setError("");
    setSuccessMsg("");
    
    const formData = new FormData();
    formData.append("target", id);
    formData.append("file", file);

    try {
      await axios.post(`${API}/admin/upload`, formData, {
        headers: {
          "X-Admin-Key": adminKey,
          "Content-Type": "multipart/form-data",
        },
      });
      setSuccessMsg(`¡Imagen "${id}" actualizada!`);
      setRefreshTimes(prev => ({ ...prev, [id]: Date.now() }));
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al subir la imagen. Verifica la API key.");
    } finally {
      setUploadingId(null);
    }
  };

  return (
    <Card testId="card-images" title="Gestión de Imágenes" icon={ImageIcon}>
      <div className="space-y-4">
        <div className="font-body text-sm font-medium text-[#fef3c7]">API Key del backend (requerida para subir)</div>
        <input
          type="password"
          value={adminKey}
          onChange={(e) => setAdminKey(e.target.value)}
          placeholder="X-Admin-Key del backend/.env"
          className="w-full rounded-md border border-white/10 bg-[#0a0805] px-3 py-2 font-body text-sm text-[#fef3c7] outline-none focus:border-[#fcd34d]"
        />
        {error && <p className="font-body text-xs text-red-400">{error}</p>}
        {successMsg && <p className="font-body text-xs text-[#fcd34d]">{successMsg}</p>}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-white/5 pt-5">
          {EDITABLE_IMAGES.map((img) => {
            const timestamp = refreshTimes[img.id] || "";
            const displayUrl = `${img.currentUrl}${timestamp ? `?t=${timestamp}` : ""}`;
            return (
              <div key={img.id} className="flex items-center justify-between gap-4 rounded-lg border border-white/5 bg-white/[0.02] p-4">
                <div className="flex items-center gap-3 min-w-0">
                  <img
                    src={displayUrl}
                    alt={img.label}
                    className="h-12 w-12 rounded object-cover border border-white/10 bg-black"
                  />
                  <div className="min-w-0">
                    <div className="font-body text-sm font-medium text-[#fef3c7] truncate">{img.label}</div>
                    <div className="font-body text-[10px] text-[#d4a373]/70">{img.id}</div>
                  </div>
                </div>
                
                <div>
                  <label className={`inline-flex items-center justify-center rounded-md border border-white/10 px-3 py-1.5 font-body text-[10px] uppercase tracking-[0.3em] text-[#fcd34d] transition hover:border-[#fcd34d]/40 hover:bg-white/5 cursor-pointer ${uploadingId === img.id ? "opacity-50 pointer-events-none" : ""}`}>
                    {uploadingId === img.id ? "Subiendo..." : "Subir"}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleUpload(img.id, e.target.files[0])}
                    />
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

/* ---------- Login screen ---------- */
function AdminLogin() {
  const { loginAdmin } = useTweaks();
  const [pwd, setPwd] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (loginAdmin(pwd)) { setErr(""); }
    else { setErr("Contraseña incorrecta"); }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#04070b] text-[#fef3c7]">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(197,160,89,0.10),transparent_70%)]" />
      <div className="grain" aria-hidden />

      <div className="relative mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-16">
        <Link to="/" data-testid="back-to-landing" className="mb-12 inline-flex items-center gap-2 font-body text-[10px] uppercase tracking-[0.4em] text-[#d4a373] hover:text-[#fcd34d]">
          <ArrowLeft size={12} /> Volver al memorial
        </Link>

        <div className="rounded-[24px] border border-white/10 bg-white/[0.03] p-8 backdrop-blur-2xl md:p-10">
          <div className="mb-8 flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#fcd34d]/10 ring-1 ring-[#fcd34d]/30">
              <Lock size={18} className="text-[#fcd34d]" />
            </span>
            <div>
              <div className="font-body text-[10px] uppercase tracking-[0.4em] text-[#c5a059]">Acceso restringido</div>
              <h1 className="font-serif-display text-3xl font-light italic">Panel de Tweaks</h1>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <label className="block">
              <span className="font-body text-xs uppercase tracking-[0.3em] text-[#d4a373]">Contraseña de administrador</span>
              <div className="mt-2 flex items-stretch gap-2">
                <input
                  data-testid="admin-password-input"
                  type={show ? "text" : "password"}
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  autoFocus
                  placeholder="••••••••"
                  className="w-full rounded-md border border-white/10 bg-[#0a0805] px-4 py-3 font-body text-sm text-[#fef3c7] outline-none focus:border-[#fcd34d]"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="flex items-center justify-center rounded-md border border-white/10 px-3 text-[#d4a373] hover:text-[#fcd34d]"
                  aria-label={show ? "Ocultar" : "Mostrar"}
                >
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </label>

            {err && (
              <p data-testid="admin-error" className="font-body text-xs text-red-400">{err}</p>
            )}

            <button
              type="submit"
              data-testid="admin-login-button"
              className="w-full rounded-md bg-[#c5a059] px-4 py-3 font-body text-sm font-medium uppercase tracking-[0.3em] text-[#04070b] transition hover:bg-[#fcd34d]"
            >
              Entrar
            </button>
          </form>

          <p className="font-body mt-6 text-[10px] leading-relaxed text-[#d4a373]/70">
            Demo: la contraseña por defecto es <code className="rounded bg-white/5 px-1.5 py-0.5 text-[#fcd34d]">valhalla2025</code>. Tu sesión se guarda localmente en este navegador.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ---------- Tweaks panel ---------- */
function TweaksPanel() {
  const { tweaks, setAppearance, setWpo, reset, logoutAdmin, saveToBackend } = useTweaks();
  const navigate = useNavigate();
  const [savedFlash, setSavedFlash] = useState(false);
  // ===== Draft state — los cambios solo se aplican al hacer "Guardar" =====
  const [draft, setDraft] = useState(tweaks);
  useEffect(() => { setDraft(tweaks); }, [tweaks]);
  const isDirty = JSON.stringify(draft) !== JSON.stringify(tweaks);
  const { appearance, wpo } = draft;

  const flash = () => { setSavedFlash(true); setTimeout(() => setSavedFlash(false), 1400); };
  const onAppearance = (patch) => setDraft((d) => ({ ...d, appearance: { ...d.appearance, ...patch } }));
  const onWpo = (patch) => setDraft((d) => ({ ...d, wpo: { ...d.wpo, ...patch } }));

  const save = async () => {
    const newTweaks = { appearance: draft.appearance, wpo: draft.wpo };
    setAppearance(draft.appearance);
    setWpo(draft.wpo);
    await saveToBackend(newTweaks);
    flash();
  };
  const discard = () => setDraft(tweaks);

  // Pregunta antes de navegar fuera si hay cambios pendientes
  const confirmLeave = (e, fn) => {
    if (isDirty && !window.confirm("Tienes cambios sin guardar. ¿Salir igualmente y perderlos?")) {
      if (e) e.preventDefault();
      return false;
    }
    if (fn) fn();
    return true;
  };

  // Atajo de teclado: Ctrl/Cmd + S + aviso al abandonar con cambios sin guardar
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (isDirty) save();
      }
    };
    const onBeforeUnload = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      // Texto ignorado por navegadores modernos pero requerido para activar el prompt
      e.returnValue = "Tienes cambios sin guardar en el panel de Tweaks. ¿Seguro que quieres salir?";
      return e.returnValue;
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [isDirty, draft]); // eslint-disable-line

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#04070b] text-[#fef3c7]">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(197,160,89,0.10),transparent_60%)]" />
      <div className="grain" aria-hidden />

      <div className="relative mx-auto max-w-5xl px-6 py-16 pb-32 md:px-10">
        {/* Header */}
        <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
          <div>
            <Link to="/" data-testid="back-to-landing" onClick={(e) => confirmLeave(e)} className="mb-4 inline-flex items-center gap-2 font-body text-[10px] uppercase tracking-[0.4em] text-[#d4a373] hover:text-[#fcd34d]">
              <ArrowLeft size={12} /> Volver al memorial
            </Link>
            <div className="font-body text-[10px] uppercase tracking-[0.5em] text-[#c5a059]">Panel de administración</div>
            <h1 className="font-serif-display mt-2 text-5xl font-light italic leading-tight md:text-6xl">
              Tweaks
            </h1>
            <p className="font-body mt-3 max-w-xl text-sm font-light text-[#d4a373]">
              Edita la apariencia y el rendimiento del memorial. Los cambios se previsualizan aquí
              y se aplican a la landing cuando pulsas <strong className="text-[#fcd34d]">Guardar</strong>.
            </p>
          </div>

          <div className="flex items-center gap-3">
            {savedFlash && (
              <span data-testid="saved-flash" className="flex items-center gap-1.5 font-body text-[10px] uppercase tracking-[0.3em] text-[#fcd34d]">
                <Check size={12} /> Guardado
              </span>
            )}
            <button
              data-testid="reset-button"
              onClick={() => { reset(); flash(); }}
              className="inline-flex items-center gap-2 rounded-md border border-white/10 px-4 py-2 font-body text-xs uppercase tracking-[0.3em] text-[#d4a373] transition hover:border-[#fcd34d]/40 hover:text-[#fcd34d]"
              title="Vuelve a los valores de fábrica"
            >
              <RotateCcw size={12} /> Restablecer
            </button>
            <button
              data-testid="logout-button"
              onClick={() => confirmLeave(null, () => { logoutAdmin(); navigate("/"); })}
              className="inline-flex items-center gap-2 rounded-md bg-white/5 px-4 py-2 font-body text-xs uppercase tracking-[0.3em] text-[#fef3c7]/70 transition hover:bg-white/10"
            >
              <LogOut size={12} /> Salir
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* ===== Apariencia y Diseño ===== */}
          <Card testId="card-appearance" title="Apariencia y Diseño" icon={Palette}>
            <Row label="Overlay de intro" hint="Pantalla negra inicial con logo y 'Celebra tu Legado'" testId="row-introOverlay">
              <Toggle testId="toggle-introOverlay" value={appearance.introOverlay} onChange={(v) => onAppearance({ introOverlay: v })} />
            </Row>
            <Row label="Logo en la intro" hint="Mostrar el logo dorado durante la pantalla de bienvenida" testId="row-logoIntro">
              <Toggle testId="toggle-logoIntro" value={appearance.showLogoIntro} onChange={(v) => onAppearance({ showLogoIntro: v })} />
            </Row>
            <Row label="Logo en la navegación" hint="Mostrar el logo en la barra superior junto al nombre de marca" testId="row-logoNav">
              <Toggle testId="toggle-logoNav" value={appearance.showLogoNav} onChange={(v) => onAppearance({ showLogoNav: v })} />
            </Row>
            <Row label="Capa de grano" hint="Textura sutil cinematográfica encima de toda la página" testId="row-grain">
              <Toggle testId="toggle-grain" value={appearance.grainOverlay} onChange={(v) => onAppearance({ grainOverlay: v })} />
            </Row>
            <Row label="Niebla volumétrica" hint="Capas de niebla en escenas frías" testId="row-fog">
              <Toggle testId="toggle-fog" value={appearance.fogEnabled} onChange={(v) => onAppearance({ fogEnabled: v })} />
            </Row>
            <Row label="Partículas de polvo dorado" hint="Pequeñas chispas que flotan en cada escena" testId="row-dust">
              <Toggle testId="toggle-dust" value={appearance.dustParticles} onChange={(v) => onAppearance({ dustParticles: v })} />
            </Row>
            <Row label="Aurora boreal final" hint="Efecto de aurora en la escena del mensaje final" testId="row-aurora">
              <Toggle testId="toggle-aurora" value={appearance.auroraEnabled} onChange={(v) => onAppearance({ auroraEnabled: v })} />
            </Row>
            <Row label="Navegación superior" hint="Mostrar la barra de navegación fija" testId="row-topnav">
              <Toggle testId="toggle-topnav" value={appearance.showTopNav} onChange={(v) => onAppearance({ showTopNav: v })} />
            </Row>
            <Row label="Barra de progreso" hint="Línea dorada en la parte superior que sigue el scroll" testId="row-progress">
              <Toggle testId="toggle-progress" value={appearance.showProgressBar} onChange={(v) => onAppearance({ showProgressBar: v })} />
            </Row>
            <Row label="Color de acento" hint="Color dorado principal de la marca" testId="row-accent">
              <ColorInput testId="input-accent" value={appearance.accentGold} onChange={(v) => onAppearance({ accentGold: v })} />
            </Row>
            <Row label="Disposición del hero" hint="Lado donde aparece el texto en la escena de llegada" testId="row-layout">
              <Select
                testId="select-layout"
                value={appearance.heroLayout}
                onChange={(v) => onAppearance({ heroLayout: v })}
                options={[
                  { value: "text-left", label: "Texto a la izquierda" },
                  { value: "text-right", label: "Texto a la derecha" },
                  { value: "centered", label: "Centrado" },
                ]}
              />
            </Row>
            <Row label="Intensidad del cristal (glassmorphism)" hint="Desenfoque de las tarjetas de Historias Vivas (px)" testId="row-glass">
              <Slider testId="slider-glass" value={appearance.glassmorphismStrength} onChange={(v) => onAppearance({ glassmorphismStrength: v })} min={0} max={48} />
            </Row>

            <div className="space-y-2 border-t border-white/5 pt-5">
              <div className="font-body text-sm font-medium text-[#fef3c7]">Eyebrow del hero</div>
              <TextInput testId="input-heroEyebrow" value={appearance.heroEyebrow} onChange={(v) => onAppearance({ heroEyebrow: v })} />
            </div>
            <div className="space-y-2">
              <div className="font-body text-sm font-medium text-[#fef3c7]">Título del hero</div>
              <TextInput testId="input-heroTitle" multiline value={appearance.heroTitle} onChange={(v) => onAppearance({ heroTitle: v })} />
              <p className="font-body text-[10px] text-[#d4a373]/70">Cada salto de línea es un renglón. La <strong>última línea</strong> se renderiza en dorado.</p>
            </div>
            <div className="space-y-2">
              <div className="font-body text-sm font-medium text-[#fef3c7]">Subtítulo del hero</div>
              <TextInput testId="input-heroSubtitle" multiline value={appearance.heroSubtitle} onChange={(v) => onAppearance({ heroSubtitle: v })} />
            </div>
            <div className="space-y-2">
              <div className="font-body text-sm font-medium text-[#fef3c7]">Frase final</div>
              <TextInput testId="input-finalQuote" multiline value={appearance.finalQuote} onChange={(v) => onAppearance({ finalQuote: v })} />
            </div>
          </Card>

          {/* ===== WPO Tweaks ===== */}
          <Card testId="card-wpo" title="WPO Tweaks" icon={Gauge}>
            <Row label="Lazy-load de imágenes" hint="Cargar imágenes solo cuando entren en pantalla. Reduce ancho de banda inicial." testId="row-lazy">
              <Toggle testId="toggle-lazy" value={wpo.lazyLoadImages} onChange={(v) => onWpo({ lazyLoadImages: v })} />
            </Row>
            <Row label="Precargar hero" hint="Da prioridad alta a la imagen de la primera escena" testId="row-preload">
              <Toggle testId="toggle-preload" value={wpo.preloadHero} onChange={(v) => onWpo({ preloadHero: v })} />
            </Row>
            <Row label="Calidad de imagen" hint="Aplica desenfoque ligero a 'baja' para reducir percepción de carga" testId="row-quality">
              <Select
                testId="select-quality"
                value={wpo.imageQuality}
                onChange={(v) => onWpo({ imageQuality: v })}
                options={[
                  { value: "low", label: "Baja" },
                  { value: "medium", label: "Media" },
                  { value: "high", label: "Alta" },
                ]}
              />
            </Row>
            <Row label="Reducir movimiento" hint="Desactiva parallax y zooms (mejora rendimiento y accesibilidad)" testId="row-reduceMotion">
              <Toggle testId="toggle-reduceMotion" value={wpo.reduceMotion} onChange={(v) => onWpo({ reduceMotion: v })} />
            </Row>
            <Row label="Autoplay de vídeos" hint="Reproducir vídeos de Historias Vivas al entrar en pantalla" testId="row-autoplay">
              <Toggle testId="toggle-autoplay" value={wpo.autoplayVideos} onChange={(v) => onWpo({ autoplayVideos: v })} />
            </Row>
            <Row label="Nº de partículas" hint="Cantidad base de partículas por escena (0 desactiva)" testId="row-particles">
              <Slider testId="slider-particles" value={wpo.particleCount} onChange={(v) => onWpo({ particleCount: v })} min={0} max={80} />
            </Row>
            <Row label="Scroll suave" hint="Activa scroll-behavior: smooth en navegación interna" testId="row-smooth">
              <Toggle testId="toggle-smooth" value={wpo.smoothScroll} onChange={(v) => onWpo({ smoothScroll: v })} />
            </Row>
            <Row label="Diferir escenas fuera de pantalla" hint="Pausa animaciones cuando una escena no está visible" testId="row-defer">
              <Toggle testId="toggle-defer" value={wpo.deferOffscreen} onChange={(v) => onWpo({ deferOffscreen: v })} />
            </Row>

            <div className="rounded-lg border border-white/5 bg-white/[0.02] p-4">
              <div className="mb-2 flex items-center gap-2 font-body text-[10px] uppercase tracking-[0.3em] text-[#fcd34d]">
                <Sparkles size={12} /> Sugerencia
              </div>
              <p className="font-body text-xs leading-relaxed text-[#d4a373]">
                Para móviles con datos limitados: activa <strong>Reducir movimiento</strong>, baja partículas a 15 y selecciona calidad <strong>Media</strong>. Las animaciones se mantienen pero la página carga ~40% más rápido.
              </p>
            </div>
          </Card>
        </div>

        {/* ===== Exportar BBDD (full-width) ===== */}
        <div className="mt-8">
          <ExportCard />
        </div>

        <p className="mt-12 text-center font-body text-[10px] uppercase tracking-[0.4em] text-[#d4a373]/50">
          Valhöll · Panel de tweaks · Sesión local de este navegador · <span className="text-[#fcd34d]/70">Atajo: Ctrl + S para guardar</span>
        </p>
      </div>

      {/* ===== Barra flotante de Guardar / Descartar ===== */}
      <div
        data-testid="save-bar"
        className={`pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-center px-4 pb-6 transition-all duration-300 ${
          isDirty ? "translate-y-0 opacity-100" : "translate-y-24 opacity-0"
        }`}
      >
        <div className="pointer-events-auto flex w-full max-w-2xl items-center justify-between gap-4 rounded-full border border-[#fcd34d]/30 bg-[#0a0805]/90 px-5 py-3 shadow-cinematic backdrop-blur-2xl">
          <div className="flex items-center gap-3">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#fcd34d] opacity-70" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#fcd34d]" />
            </span>
            <div className="font-body text-xs leading-tight text-[#fef3c7]">
              <div className="font-medium">Cambios sin guardar</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#d4a373]/80">
                Pulsa Guardar para aplicarlos a la landing
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              data-testid="discard-button"
              onClick={discard}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 font-body text-[10px] uppercase tracking-[0.3em] text-[#d4a373] transition hover:border-white/30 hover:text-[#fef3c7]"
            >
              Descartar
            </button>
            <button
              data-testid="save-button"
              onClick={save}
              className="inline-flex items-center gap-2 rounded-full bg-[#c5a059] px-5 py-2.5 font-body text-[10px] font-semibold uppercase tracking-[0.3em] text-[#04070b] transition hover:bg-[#fcd34d]"
            >
              <Save size={12} /> Guardar cambios
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Page entry ---------- */
export default function AdminTweaks() {
  const { isAdmin } = useTweaks();
  return isAdmin ? <TweaksPanel /> : <AdminLogin />;
}
